












































































import { difference, every, filter, find, get, keyBy, map } from 'lodash';
import { BInputGroup, VBTooltip } from 'bootstrap-vue';

import ZenButton from '@reciprocity/button';
import ZenMultiselect from '@reciprocity/multiselect';

import { getCellPresenter } from '@/components/listing/AttributeCellPresenterConfig';
import { mapOrder } from '@/utils';

import * as FilterPresenters from './presenters';
import { customAttributeFilterPresenterConfig, filterPresenterConfig } from '../utils/filterPresenterConfig';
import { dateFiltersMeta, filtersMeta } from '../utils/filtersMeta';
import { filterPresenterMeta } from '../utils/filterPresenterMeta';
import { getFilterPresenterType } from '../utils/filterPresenterUtils';
import { FilterPresenterProps } from './types';
import { ZenFilter } from '../types';
import { OBJECT_TYPE_ATTRIBUTES_QUERY } from './queries';

export default {
  name: 'ListingFilter',
  components: {
    BInputGroup,

    ZenButton,
    ZenMultiselect,

    ...FilterPresenters,
  },
  directives: {
    BTooltip: VBTooltip,
  },
  props: {
    objectType: {
      // In ModelMetaSchema.modelSingular form
      type: String,
      required: true,
    },
    activeColumns: {
      type: Array,
      required: true,
    },
    filter: {
      type: Object as () => ZenFilter,
      required: true,
    },
    displayValidation: {
      type: Boolean,
      default: true,
    },
    removable: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    showValueInput(): boolean {
      const opName = this.filterData.operator && this.filterData.operator.name;
      if (this.preselectedValue(opName)) return false;
      if (this.hasOperatorOnly(opName)) return false;
      return true;
    },
    fieldsCount(): number {
      return this.showValueInput ? 3 : 2;
    },
    fieldClass(): string {
      return `field-${this.fieldsCount}`;
    },

    validationTitle(): string {
      return this.valid(this.filter) ? 'Filter is applied' : 'Filter is not applied';
    },
    validationTooltip() {
      return {
        title: this.validationTitle,
        trigger: 'hover',
        interactive: false,
        disabled: this.pristine(this.filter),
      };
    },

    attributesMeta() {
      return keyBy(this.objectTypeAttributes, 'name');
    },
    filterData() {
      const attribute = this.attributesMeta[this.filter.attribute];
      const operatorsMeta = keyBy(this.operators(attribute), 'name');

      return {
        attribute,
        operator: operatorsMeta[this.filter.operator],
        value: this.filter.value,
      };
    },
    computedAttributes() {
      const filtersAttributes = filter(this.objectTypeAttributes, attr => attr.filterable);
      const inactiveColumns = difference(
        map(filtersAttributes, a => a.name),
        this.activeColumns
      );

      return [
        {
          attributesGroup: 'Visible attributes and mappings',
          attributes: mapOrder(filtersAttributes, this.activeColumns, 'name'),
        },
        {
          attributesGroup: 'Non-visible attributes and mappings',
          attributes: mapOrder(filtersAttributes, inactiveColumns, 'name'),
        },
      ];
    },
  },
  methods: {
    hasOperatorOnly(operator): boolean {
      const opName: string = operator ? operator : this.filter.operator;
      const filterMeta = find(filtersMeta, filterMeta => filterMeta.operator === opName);

      return get(filterMeta, 'operatorOnly', false);
    },
    preselectedValue(operator): string | undefined {
      const opName: string = operator ? operator : this.filter.operator;
      const filterMeta = find(filtersMeta, filterMeta => filterMeta.operator === opName);

      return get(filterMeta, 'value');
    },

    pristine(filter: ZenFilter): boolean {
      const filterPristine = filter => every(Object.values(filter), v => !v);

      return filterPristine(filter);
    },
    valid(filter: ZenFilter): boolean {
      /**
       * We consider `0` value valid for legacy reasons for filtering Boolean
       * type attributes.
       */
      const valueIsValid = v => v === 0 || !!v;

      // TODO: IOC, do this cleaner (use different checks if mapping or non-mapping attr)
      const filterValid = (filter: ZenFilter) =>
        filter.attribute && filter.attribute.startsWith('mappings.')
          ? filter.operator === '='
            ? !!filter.operator && !!filter.value
            : !!filter.operator
          : every(Object.values(filter), valueIsValid);

      return filterValid(filter);
    },
    onChange(filterProperty: string, event: { name: string }): void {
      const handleAttributeChange = (newAttribute: string, oldAttribute: string) => {
        // Unset operator dropdown if attribute is not selected
        if (newAttribute == null) {
          this.filter.operator = null;
          return;
        }

        // Do nothing if same attribute remains selected
        if (newAttribute == oldAttribute) return;

        // Preselect first operator
        const operators = this.operators(this.attributesMeta[newAttribute]);
        this.filter.operator = get(operators, '[0].name', null);

        // Reset the value
        this.filter.value = '';
      };

      const handleOperatorChange = (newOperator, oldOperator) => {
        const newPreselectedValue: string | undefined = this.preselectedValue(newOperator);
        const oldPreselectedValue: string | undefined = this.preselectedValue(oldOperator);

        // Reset value only when new operator has no preselected value
        if (oldPreselectedValue && !newPreselectedValue) {
          this.filter.value = '';
        }
      };

      if (filterProperty === 'attribute') {
        handleAttributeChange(event.name, this.filter.attribute);
      }
      if (filterProperty === 'operator') {
        handleOperatorChange(event.name, this.filter.operator);
      }

      let filter = {
        ...this.filter,
        ...{ [filterProperty]: event.name },
      };

      const value = this.preselectedValue(filter.operator);
      if (value) {
        filter = {
          ...filter,
          ...{ value },
        };
      }

      const meta = {
        pristine: this.pristine(filter),
        valid: this.valid(filter),
      };

      this.$emit('change', { value: filter, meta });
    },
    onRemoveClick(): void {
      this.$emit('remove');
    },

    // TODO: temp, removed after refactoring attributes and types
    filterPresenterType(attribute): String {
      if (!attribute) return 'Text';

      if (attribute.customAttributeType) {
        return customAttributeFilterPresenterConfig[attribute.customAttributeType];
      }

      const cellPresenter: string = getCellPresenter(this.objectType, attribute.name);
      return getFilterPresenterType(cellPresenter);
    },
    // TODO: use from gql local cache after adding filters defintions there
    operators(attribute) {
      const filters: Filter[] = filterPresenterMeta[this.filterPresenterType(attribute)];

      let filtersMetaForAttrType = filtersMeta;
      if (this.filterPresenterType(attribute) === 'Date') {
        filtersMetaForAttrType = dateFiltersMeta;
      }

      const filterOperators = map(filters, filter => ({
        value: filtersMetaForAttrType[filter].operator,
        text: filtersMetaForAttrType[filter].title,
      }));

      return map(filterOperators, operator => ({ title: operator.text, name: operator.value }));
    },
    // TODO: temp, removed after refactoring attributes and types
    presenterComponent(attribute) {
      if (!attribute) return 'TextPresenter';

      if (attribute.customAttributeType) {
        const caFilterPresenter: string = customAttributeFilterPresenterConfig[attribute.customAttributeType];
        return `${caFilterPresenter}Presenter`;
      }

      const cellPresenter = getCellPresenter(this.objectType, attribute.name);
      const filterPresenter: string =
        this.objectType == 'Revision' &&
        (attribute.name == 'modifiedObject' || attribute.name == 'mappingFrom' || attribute.name == 'mappingTo')
          ? 'Text'
          : filterPresenterConfig[cellPresenter];

      return `${filterPresenter}Presenter`;
    },

    presenterProps(attribute): FilterPresenterProps {
      return {
        objectType: this.objectType,
        attribute: attribute ? attribute.name : '',
        operator: get(this.filterData, 'operator.name', null),
        placeholder: 'Value',
        disabled: !attribute,
      };
    },
  },
  apollo: {
    objectTypeAttributes: {
      query: OBJECT_TYPE_ATTRIBUTES_QUERY,
      variables() {
        return {
          type: this.objectType,
          context: 'SOR_LISTING',
        };
      },
      update: data => data.objectType.attrs,
    },
  },
};
